/**
 * B78 — charts recovered from the DS v2 gallery (B51, f0e4a53) as real,
 * token-driven components. Hand-rolled SVG on the --transact-* language
 * (no chart library — a new runtime dependency is an escalation
 * category, and none of these need one). Every chart is an image to AT:
 * role="img" plus a required human label; data that cannot draw
 * degrades to an honest note, never a broken picture. Colors ride t-*
 * classes styled in tokens.css, so light/dark and embed themes apply by
 * construction.
 */
import { useId, type ReactNode } from "react";
import {
  LiveMap,
  diagramLngLats,
  liveOrdersFrame,
  type LiveOrdersPulse,
  type LiveOrdersTerminal,
} from "./map/liveMap.js";

export interface ChartPoint {
  readonly label: string;
  readonly value: number;
}

export type ChartTone = "accent" | "ink" | "muted";

function clamp01(v: number): number {
  return Number.isFinite(v) ? Math.min(1, Math.max(0, v)) : 0;
}

/* ------------------------------------------------------------ Sparkline */

/**
 * The inline trend — a KPI's last-N-periods shape at a glance. B355 grew
 * the FIGURE root + footnote slot (§9.7 reads a chart's own sentence off
 * its root) and the DRAWN flat face: a window of zeros is a line along
 * the floor — the true picture of a quiet day — never a missing element.
 */
export function Sparkline(props: {
  readonly points: readonly number[];
  readonly ariaLabel: string;
  /** §9.7 — the card's sentence when the spark is the card's one visual. */
  readonly footnote?: string;
  /** §5 — the dashed PROJECTION tail after the last actual point. */
  readonly forecast?: readonly number[];
  /** The KPI redline's card-wide 46px visual band; `card` — a rail card's card-wide 34 (551:2's THIS WEEK, B789). */
  readonly size?: "band" | "card";
}): ReactNode {
  const pts = props.points.filter((v) => Number.isFinite(v));
  const fc = (props.forecast ?? []).filter((v) => Number.isFinite(v));
  const W = 120;
  const H = 32;
  const pad = 2;
  const drawable = pts.length >= 2 ? pts : [0, 0];
  const all = [...drawable, ...fc];
  const hi = Math.max(...all);
  const lo = Math.min(...all);
  const span = hi - lo || 1;
  const cx = (i: number): number => pad + (i * (W - 2 * pad)) / Math.max(1, all.length - 1);
  const cy = (v: number): number => H - pad - ((v - lo) / span) * (H - 2 * pad);
  const coord = (v: number, i: number): string => `${cx(i).toFixed(1)},${cy(v).toFixed(1)}`;
  const coords = drawable.map(coord).join(" ");
  const fcCoords =
    fc.length === 0 ? undefined : [drawable[drawable.length - 1]!, ...fc].map((v, i) => coord(v, drawable.length - 1 + i)).join(" ");
  const svg = (
    /* preserveAspectRatio none + non-scaling strokes: the spark fills the
       card's own band (the KPI redline draws it card-wide, 46 tall) while
       the 2px ink stays 2px at any stretch. */
    <svg className={`t-sparkline${props.size === undefined ? "" : ` t-sparkline--${props.size}`}`} viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" {...(props.footnote === undefined ? { role: "img", "aria-label": props.ariaLabel } : { "aria-hidden": true })}>
      <title>{props.ariaLabel}</title>
      <polyline className="t-sparkline-line" vectorEffect="non-scaling-stroke" points={coords} />
      {fcCoords !== undefined ? <polyline className="t-sparkline-forecast" vectorEffect="non-scaling-stroke" points={fcCoords} /> : null}
    </svg>
  );
  if (props.footnote === undefined) return svg;
  return (
    <figure className={`t-sparkline-fig${props.size === undefined ? "" : ` t-sparkline-fig--${props.size}`}`} role="img" aria-label={props.ariaLabel}>
      {svg}
      <figcaption className="t-footnote t-sparkline-note">{props.footnote}</figcaption>
    </figure>
  );
}

/* ---------------------------------------------------------- ProgressBar */

/** A labeled meter row: name, toned fill, supporting detail. */
export function ProgressBar(props: {
  /** B355 review #3 — the departures ROW register: time · slim 6px track · tracked count.
   * "thin" is the leaderboard row (THIS WEEK BY PRODUCT): name · 3px track · count.
   * B637 — "stack" is 530:2's BY SURFACE row: name and figures on one line, the bar under them.
   * B708 (4) — "meter" is 640:2's BY TIER row: the same anatomy in the report's quieter
   * registers (SS3 Light 13.5 name, 12.5 figures) over an 8px rounded bar. */
  readonly size?: "row" | "thin" | "track" | "stack" | "meter" | "funnel";
  readonly label: string;
  readonly fraction: number;
  readonly detail?: string;
  /** B355 — status tones joined the palette so a row can wear a verdict
   * (pace, health) without an app forking the track. */
  readonly tone?: ChartTone | StatusTone;
  /** B549 (8) — the track's name in a redline sidecar (448:6's headcount bar). */
  readonly redline?: string;
  /**
   * B525 (c) — the prior window's fill, drawn BEHIND the current one (530:2's
   * "ghost prior bar"), so a comparison reads in the bar itself. Decoration
   * for the eye only: the meter's value stays `fraction`, and the consumer
   * says the prior in words (its detail).
   */
  readonly ghost?: number;
  /** B787 — "funnel" is 530:2's step row: the count on the right (figure) over its caption. */
  readonly figure?: string;
  readonly caption?: string;
}): ReactNode {
  const pct = Math.round(clamp01(props.fraction) * 100);
  if (props.size === "funnel") {
    /* B787 (530:2) — name · the bar with the prior window as its own thin
       bar UNDER it · the count over its caption. The prior bar is the eye's;
       the consumer's caption says the prior in words (B287). */
    return (
      <div className={`t-progress t-progress--${props.tone ?? "accent"} t-progress--funnel`}>
        <span className="t-progress-name">{props.label}</span>
        <div className="t-progress-bars">
          <div
            className="t-progress-bar"
            role="meter"
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={pct}
            aria-label={props.label}
          >
            <div className="t-progress-fill" style={{ width: `${pct}%` }} />
          </div>
          {props.ghost === undefined ? null : (
            <div className="t-progress-prior" aria-hidden="true" style={{ width: `${Math.round(clamp01(props.ghost) * 100)}%` }} />
          )}
        </div>
        <span className="t-progress-figure">{props.figure ?? ""}</span>
        <span className="t-progress-caption">{props.caption ?? ""}</span>
      </div>
    );
  }
  /* B549 (8) — "track" is the bare register: the frame draws the bar alone
     (its figure sits above it), so the name is the meter's AT name only. */
  const bare = props.size === "track";
  return (
    <div className={`t-progress t-progress--${props.tone ?? "accent"}${props.size === undefined ? "" : ` t-progress--${props.size}`}`}>
      {bare ? null : <span className="t-progress-name">{props.label}</span>}
      <div
        className="t-progress-bar"
        {...(props.redline !== undefined ? { "data-redline": props.redline } : {})}
        role="meter"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={pct}
        aria-label={props.label}
      >
        {props.ghost === undefined ? null : (
          <div className="t-progress-ghost" aria-hidden="true" style={{ width: `${Math.round(clamp01(props.ghost) * 100)}%` }} />
        )}
        <div className="t-progress-fill" style={{ width: `${pct}%` }} />
      </div>
      {!bare && props.detail !== undefined && <span className="t-progress-detail">{props.detail}</span>}
    </div>
  );
}

/* -------------------------------------------------------------- Heatmap */

export interface HeatmapColumn {
  readonly label: string;
  readonly cells: readonly { readonly intensity: number; readonly tone?: ChartTone }[];
}

/**
 * The cohort grid: toned cells, intensity as opacity with a visible floor.
 * B594 (3) — optional labels: `rowLabels` down the side (one per cell row)
 * and, with them, each column's label underneath (530:242's weekday × hour
 * grid). Without `rowLabels` the grid is drawn exactly as before.
 */
export function Heatmap(props: {
  readonly columns: readonly HeatmapColumn[];
  readonly ariaLabel: string;
  readonly rowLabels?: readonly string[];
  /** B785 (530:242) — "wide": 40 × 22 cells, the column names ABOVE, horizontal. */
  readonly size?: "wide";
}): ReactNode {
  const labelled = props.rowLabels !== undefined;
  const wide = props.size === "wide";
  const colLabel = (label: string): ReactNode =>
    labelled ? (
      <span className="t-heatmap-collabel" aria-hidden="true">
        {label}
      </span>
    ) : null;
  return (
    <div className={`t-heatmap${wide ? " t-heatmap--wide" : ""}`} role="img" aria-label={props.ariaLabel}>
      {labelled ? (
        <div className="t-heatmap-rowlabels" aria-hidden="true">
          {wide ? <span className="t-heatmap-rowlabel" /> : null}
          {props.rowLabels?.map((l, i) => (
            <span key={`${l}-${i}`} className="t-heatmap-rowlabel">
              {l}
            </span>
          ))}
          {wide ? null : <span className="t-heatmap-rowlabel" />}
        </div>
      ) : null}
      {props.columns.map((col, c) => (
        <div key={`${col.label}-${c}`} className="t-heatmap-col" title={col.label}>
          {wide ? colLabel(col.label) : null}
          {col.cells.map((cell, i) => (
            <span
              key={i}
              className={`t-heatmap-cell t-heatmap-cell--${cell.tone ?? "accent"}`}
              style={{ opacity: Math.max(0.08, clamp01(cell.intensity)) }}
            />
          ))}
          {wide ? null : colLabel(col.label)}
        </div>
      ))}
    </div>
  );
}

/* ---------------------------------------------------------- CapacityBar */

/** B107 — the pacing verdict a caller judged for this bar (alertJudgment's
 * capacityPace or a sibling): green must mean pacing well, never seats-left. */
export type CapacityPace = "early" | "ahead" | "on-pace" | "behind";

/** Sold | held | left as one stacked bar — the slot's story at a glance. */
/** B124: the ONE derivation of the pace verdict's operator words. */
export const paceWords: Readonly<Record<CapacityPace, string>> = Object.freeze({
  early: "early days",
  ahead: "selling well",
  "on-pace": "on pace",
  behind: "behind pace",
});

export function CapacityBar(props: {
  readonly sold: number;
  readonly held: number;
  readonly left: number;
  /** B179: the pool this bar counts ("Seats", "Bicycles") — REQUIRED reading
   * wherever one row carries more than one bar; a lone bar may stay bare. */
  readonly label?: string;
  readonly ariaLabel?: string;
  /** Optional pacing verdict — recolors the bar (sold wears the verdict,
   * empty seats go neutral) and joins the counts note in operator words. */
  readonly pace?: CapacityPace;
  /** B124: the visible counts note is part of the bar's meaning — a bar
   * without numbers reads as a lie. Dense grids opt out explicitly. */
  readonly counts?: boolean;
}): ReactNode {
  const sold = Math.max(0, props.sold);
  const held = Math.max(0, props.held);
  const left = Math.max(0, props.left);
  const total = sold + held + left;
  const countsText = `${sold} sold · ${held} held · ${left} left${props.pace === undefined ? "" : ` · ${paceWords[props.pace]}`}`;
  const label = props.ariaLabel ?? (props.label === undefined ? countsText : `${props.label}: ${countsText}`);
  const name =
    props.label === undefined ? null : (
      <span className="t-capacity-name" aria-hidden="true">
        {props.label}
      </span>
    );
  const showCounts = props.counts !== false;
  const note = showCounts ? (
    <span className="t-capacity-note" aria-hidden="true">
      {countsText}
    </span>
  ) : null;
  const rootClass = props.pace === undefined ? "t-capacity" : `t-capacity t-capacity--pace-${props.pace}`;
  if (total === 0) {
    return (
      <>
        {name}
        <div className={`${rootClass} t-capacity--empty`} role="img" aria-label={label}>
          <span className="t-chart-note">No capacity on sale.</span>
        </div>
      </>
    );
  }
  const width = (v: number): string => `${((v / total) * 100).toFixed(1).replace(/\.0$/, "")}%`;
  return (
    <>
      {name}
      <div className={rootClass} role="img" aria-label={label}>
        {sold > 0 && <span className="t-capacity-seg t-capacity-seg--sold" style={{ width: width(sold) }} />}
        {held > 0 && <span className="t-capacity-seg t-capacity-seg--held" style={{ width: width(held) }} />}
        {left > 0 && <span className="t-capacity-seg t-capacity-seg--left" style={{ width: width(left) }} />}
      </div>
      {note}
    </>
  );
}

/* ════════════════════════════════════════════════════════════════════════
 * B353(c) — the DESIGN-V2 §5 chart grammar. Everything below is skin v2.
 * B367 tail (2026-08-19): AreaChart and Gauge are DELETED — LineChart and
 * Gauge220 succeeded them and the last consumers (the gallery demos) are
 * retired. Sparkline stayed and grew v2 anatomy (the KPI sparks: figure
 * root, §9.7 footnote, dashed forecast tail). ProgressBar stayed by
 * RULING — the redline gave it the frame's own registers (size="row" =
 * NEXT DEPARTURES, size="thin" = THIS WEEK BY PRODUCT), so it is v2, not
 * a twin awaiting CapacityBar.
 *
 * Shared v2 rules, walled in charts-v2.test.tsx:
 * - the plain-language footnote is a REQUIRED prop (§9.7 — an unexplained
 *   number is unfinished), rendered inside the chart root so testkit's
 *   footnoteViolations sees it by construction;
 * - root class names are the ones CHART_ROOT_CLASS registered for B353
 *   (t-gauge220, t-markertrack, t-donut, t-cmpbars, t-forecast), so the
 *   §9 walls judge these charts by NAME;
 * - data that cannot draw degrades to the honest t-chart--empty note.
 * ════════════════════════════════════════════════════════════════════════ */

/** The §5 status tones a chart may spend — THE outlier wears one, the rest stay neutral. */
export type StatusTone = "good" | "warn" | "alert";

/** The honest empty: same note the v1 charts speak, on the chart's own root class. */
function emptyChart(rootClass: string, ariaLabel: string): ReactNode {
  return (
    <div className={`${rootClass} t-chart t-chart--empty`} role="img" aria-label={ariaLabel}>
      <span className="t-chart-note">Not enough data to draw yet.</span>
    </div>
  );
}

const pctString = (fraction: number): string => `${(clamp01(fraction) * 100).toFixed(1).replace(/\.0$/, "")}%`;

/* ------------------------------------------------------------- LineChart */

/**
 * The §5 line: 2px solid actuals, dashed forecast FROM the now-marker
 * (1px vertical + triangle), hairline grid, NO area fill, per-point
 * frosted tooltip (time caps + big thin value). Succeeds AreaChart.
 */
export function LineChart(props: {
  readonly points: readonly ChartPoint[];
  /** Dashed continuation after the last actual point; omitted = no now-marker. */
  readonly forecast?: readonly ChartPoint[];
  /** The plain-language sentence under the drawing (§9.7). */
  readonly footnote: string;
  readonly ariaLabel: string;
  readonly formatValue?: (value: number) => string;
  readonly height?: number;
  /** §5 axis ticks: a label every N points (the day curve's 12 AM · 4 AM ·
   * 8 AM …); absent = the first/last pair. */
  readonly tickEvery?: number;
}): ReactNode {
  const actual = props.points;
  const forecast = props.forecast ?? [];
  const lastActual = actual[actual.length - 1];
  if (actual.length < 2 || lastActual === undefined) return emptyChart("t-line", props.ariaLabel);
  const all = [...actual, ...forecast];
  const first = all[0];
  const last = all[all.length - 1];
  const W = 600;
  const H = props.height ?? 200;
  const pad = { l: 8, r: 8, t: 16, b: 24 };
  const values = all.map((p) => p.value);
  const hi = Math.max(...values);
  const lo = Math.min(...values);
  const span = hi - lo || 1;
  const x = (i: number): number => pad.l + (i * (W - pad.l - pad.r)) / (all.length - 1);
  const y = (v: number): number => H - pad.b - ((v - lo) / span) * (H - pad.t - pad.b);
  const pathOf = (pts: readonly ChartPoint[], offset: number): string =>
    pts.map((p, i) => `${i === 0 ? "M" : "L"}${x(i + offset).toFixed(1)} ${y(p.value).toFixed(1)}`).join(" ");
  const nowX = x(actual.length - 1);
  const fmt = props.formatValue ?? ((v: number): string => String(v));
  const gridYs = [0.25, 0.5, 0.75].map((f) => pad.t + f * (H - pad.t - pad.b));
  return (
    <div className="t-line t-chart" role="img" aria-label={props.ariaLabel}>
      <svg className="t-line-svg" viewBox={`0 0 ${W} ${H}`} aria-hidden="true" preserveAspectRatio="xMidYMid meet">
        {gridYs.map((gy) => (
          <line key={gy} className="t-line-grid" x1={pad.l} x2={W - pad.r} y1={gy.toFixed(1)} y2={gy.toFixed(1)} />
        ))}
        <path className="t-line-actual" d={pathOf(actual, 0)} />
        {forecast.length > 0 && (
          <>
            <line className="t-line-now" x1={nowX.toFixed(1)} x2={nowX.toFixed(1)} y1={pad.t} y2={H - pad.b} />
            <path
              className="t-line-nowtri"
              d={`M${(nowX - 4).toFixed(1)} ${pad.t} L${(nowX + 4).toFixed(1)} ${pad.t} L${nowX.toFixed(1)} ${pad.t + 6} Z`}
            />
            <path className="t-line-forecast" d={pathOf([lastActual, ...forecast], actual.length - 1)} />
          </>
        )}
        {all.map((p, i) => {
          const px = x(i);
          const py = y(p.value);
          const tipX = Math.min(Math.max(px - 60, pad.l), W - pad.r - 120);
          const tipY = Math.max(py - 58, 2);
          return (
            <g key={`${p.label}-${i}`} className="t-line-pt">
              <circle
                className={i >= actual.length ? "t-line-dot t-line-dot--forecast" : "t-line-dot"}
                cx={px.toFixed(1)}
                cy={py.toFixed(1)}
                r={7}
              >
                <title>{`${p.label}: ${fmt(p.value)}`}</title>
              </circle>
              <g className="t-line-tip">
                <rect className="t-line-tipbox" x={tipX.toFixed(1)} y={tipY.toFixed(1)} width={120} height={48} rx={8} />
                <text className="t-line-tiptime" x={(tipX + 12).toFixed(1)} y={(tipY + 18).toFixed(1)}>
                  {p.label}
                </text>
                <text className="t-line-tipvalue" x={(tipX + 12).toFixed(1)} y={(tipY + 40).toFixed(1)}>
                  {fmt(p.value)}
                </text>
              </g>
            </g>
          );
        })}
        {props.tickEvery !== undefined && props.tickEvery > 0 ? (
          all
            .map((p, i) => ({ p, i }))
            .filter(({ i }) => i % props.tickEvery! === 0)
            .map(({ p, i }) => (
              <text key={i} className="t-line-axis" x={x(i).toFixed(1)} y={H - 6} textAnchor={i === 0 ? "start" : "middle"}>
                {p.label}
              </text>
            ))
        ) : (
          <>
            <text className="t-line-axis" x={pad.l} y={H - 6}>
              {first?.label}
            </text>
            <text className="t-line-axis" x={W - pad.r} y={H - 6} textAnchor="end">
              {last?.label}
            </text>
          </>
        )}
      </svg>
      <p className="t-footnote t-line-note">{props.footnote}</p>
    </div>
  );
}

/* -------------------------------------------------------------- Gauge220 */

/**
 * The §5 gauge: a 220° arc — base track, solid value arc, fine ticks
 * (majors heavier than minors), needle, center thin numeral. Succeeds
 * the v1 half-circle Gauge.
 */
export function Gauge220(props: {
  readonly value: number;
  readonly max: number;
  readonly centerValue: string;
  readonly centerLabel?: string;
  /**
   * The value arc's tone. Tone-less is NEUTRAL — the frame's thin white arc
   * (B367 §5 miss 1): accent is the one-primary-action color and is painted
   * only where a screen means it, a status tone only where a verdict does.
   */
  readonly tone?: "neutral" | "accent" | StatusTone;
  readonly footnote: string;
  readonly ariaLabel: string;
  /** B355 — the KPI-card dial: the center numeral drops to a chart LABEL
   * (the frame's 70%), not §3's 34px display numeral. */
  readonly size?: "small" | "compact";
}): ReactNode {
  /* B781 — "compact" is 113:2's rail dial: 140 wide on the card's right,
     its figure on the figure-m step, the sentence beside it (tokens.css). */
  if (!Number.isFinite(props.max) || props.max <= 0) return emptyChart("t-gauge220", props.ariaLabel);
  const frac = clamp01(props.value / props.max);
  const cx = 150;
  const cy = 150;
  const r = 108;
  const START = 200; // degrees, math orientation; the arc runs 200° → −20°
  const SWEEP = 220;
  const point = (deg: number, rad: number): readonly [number, number] => [
    cx + rad * Math.cos((deg * Math.PI) / 180),
    cy - rad * Math.sin((deg * Math.PI) / 180),
  ];
  const arc = (from: number, to: number): string => {
    const [x0, y0] = point(from, r);
    const [x1, y1] = point(to, r);
    return `M${x0.toFixed(1)} ${y0.toFixed(1)} A${r} ${r} 0 ${from - to > 180 ? 1 : 0} 1 ${x1.toFixed(1)} ${y1.toFixed(1)}`;
  };
  const valueTo = START - SWEEP * frac;
  const ticks = [];
  for (let i = 0; i <= 22; i += 1) {
    const deg = START - 10 * i;
    const major = i % 5 === 0;
    const [tx0, ty0] = point(deg, r + 7);
    const [tx1, ty1] = point(deg, r + (major ? 17 : 12));
    ticks.push(
      <line
        key={i}
        className={major ? "t-gauge220-tick t-gauge220-tick--major" : "t-gauge220-tick"}
        x1={tx0.toFixed(1)}
        y1={ty0.toFixed(1)}
        x2={tx1.toFixed(1)}
        y2={ty1.toFixed(1)}
      />,
    );
  }
  /*
   * B367 §5 miss 2: the needle ran from the dial CENTRE outward, straight
   * through the numeral that sits there. It is a hub-to-rim segment now —
   * HUB_R clears the center numeral's box on every bearing the 220° sweep
   * produces, and the outer end is unchanged, so the bearing the gauge reads
   * is exactly the one it read before.
   */
  // B781 — the compact dial draws 140 wide with the figure at figure-m, so
  // a 46 hub put the needle's foot inside "0.0%" (measured on dev). The
  // frame's rail dial (113:2) draws a short rim needle: its foot at ~0.65 r.
  const HUB_R = props.size === "compact" ? 72 : 46;
  const [hx, hy] = point(valueTo, HUB_R);
  const [nx, ny] = point(valueTo, r - 22);
  return (
    <div className={props.size === undefined ? "t-gauge220" : `t-gauge220 t-gauge220--${props.size}`} role="img" aria-label={props.ariaLabel}>
      <div className="t-gauge220-dial">
        <svg viewBox="0 0 300 212" aria-hidden="true" preserveAspectRatio="xMidYMid meet">
          <path className="t-gauge220-track" d={arc(START, START - SWEEP)} />
          {frac > 0 && <path className={`t-gauge220-arc t-gauge220-arc--${props.tone ?? "neutral"}`} d={arc(START, valueTo)} />}
          {ticks}
          <line className="t-gauge220-needle" x1={hx.toFixed(1)} y1={hy.toFixed(1)} x2={nx.toFixed(1)} y2={ny.toFixed(1)} />
        </svg>
        <div className="t-gauge220-center" aria-hidden="true">
          <b className="t-gauge220-numeral">{props.centerValue}</b>
          {props.centerLabel !== undefined && <span className="t-gauge220-centerlabel">{props.centerLabel}</span>}
        </div>
      </div>
      <p className="t-footnote t-gauge220-note">{props.footnote}</p>
    </div>
  );
}

/* ----------------------------------------------------------- MarkerTrack */

/**
 * The §5 marker track: big thin value, 2px range track with a value
 * marker, min/max caps, and the consequence footnote ("Drops to 0 at
 * 4:00 PM") — the one-number card for capacities and countdowns.
 */
export function MarkerTrack(props: {
  readonly value: string;
  /** Review #3 — the refunds card's red numeral + the 28×3 alert fill at the marker. */
  readonly tone?: "alert";
  /** Marker position along the track, 0..1 (clamped). ABSENT = the
   * absent-marker face (B355): the track drawn, no marker — a zero day
   * keeps its empty track without a marker claiming "just arrived". */
  readonly fraction?: number;
  readonly minLabel: string;
  readonly maxLabel: string;
  readonly footnote: string;
  readonly ariaLabel: string;
}): ReactNode {
  return (
    <div className="t-markertrack" role="img" aria-label={props.ariaLabel}>
      <span className={props.tone === undefined ? "t-markertrack-numeral" : "t-markertrack-numeral t-markertrack-numeral--alert"} aria-hidden="true">
        {props.value}
      </span>
      <div className="t-markertrack-track" aria-hidden="true">
        {props.fraction !== undefined ? (
          <>
            {props.tone === "alert" ? (
              <span className="t-markertrack-fill" style={{ left: pctString(props.fraction) }} />
            ) : null}
            <span className="t-markertrack-marker" style={{ left: pctString(props.fraction) }} />
          </>
        ) : null}
      </div>
      <div className="t-markertrack-range" aria-hidden="true">
        <span className="t-markertrack-min">{props.minLabel}</span>
        <span className="t-markertrack-max">{props.maxLabel}</span>
      </div>
      <p className="t-footnote t-markertrack-note">{props.footnote}</p>
    </div>
  );
}

/* ----------------------------------------------------------------- Donut */

export interface DonutSegment {
  readonly label: string;
  readonly value: number;
  readonly tone?: ChartTone;
}

const DONUT_TONES: readonly ChartTone[] = ["accent", "ink", "muted"];

/**
 * The §5 donut: thin ring (inner radius 90%), 6° segment gaps, center
 * fact, legend of swatch/label/share.
 */
export function Donut(props: {
  readonly segments: readonly DonutSegment[];
  readonly centerValue: string;
  readonly centerLabel?: string;
  readonly footnote: string;
  readonly ariaLabel: string;
  readonly formatShare?: (fraction: number) => string;
}): ReactNode {
  const segs = props.segments.filter((s) => Number.isFinite(s.value) && s.value > 0);
  const total = segs.reduce((sum, s) => sum + s.value, 0);
  /* B355 / PAGE REVIEW #1 — the ZERO FACE, drawn rather than dodged.
     The frame draws this ring in every state, and a ring with nothing to
     divide is not a broken chart: it is the true shape of a quiet
     morning. So the track is drawn in the faint chart ink, the center
     keeps the answered total the shares would be OF, and the footnote —
     the caller's own §9.7 sentence — says in words what the empty ring
     means. No legend: a row reading "0%" is arithmetic performed on
     nothing, and the share denominator here is zero.

     What this face does NOT claim is that a read happened. A caller
     holding no answer draws no Donut at all; that call belongs to the
     screen, which alone knows whether its read refused (B203(b)). */
  if (segs.length === 0 || total <= 0) {
    return (
      <div className="t-donut t-donut--zero" role="img" aria-label={props.ariaLabel}>
        <div className="t-donut-ring">
          <svg className="t-donut-svg" viewBox="0 0 200 200" aria-hidden="true" preserveAspectRatio="xMidYMid meet">
            <circle className="t-donut-track" cx={100} cy={100} r={76} />
          </svg>
          <div className="t-donut-center" aria-hidden="true">
            <b className="t-donut-numeral">{props.centerValue}</b>
            {props.centerLabel !== undefined && <span className="t-donut-centerlabel">{props.centerLabel}</span>}
          </div>
        </div>
        <p className="t-footnote t-donut-note">{props.footnote}</p>
      </div>
    );
  }
  const cx = 100;
  const cy = 100;
  const rmid = 76; // ring centreline; stroke width 8 ≈ inner radius 90% of 80
  const gapDeg = segs.length > 1 ? 6 : 0;
  const usable = 360 - gapDeg * segs.length;
  const point = (deg: number): readonly [number, number] => [
    cx + rmid * Math.cos((deg * Math.PI) / 180),
    cy - rmid * Math.sin((deg * Math.PI) / 180),
  ];
  let cursor = 90;
  const arcs = segs.map((s, i) => {
    const tone = s.tone ?? DONUT_TONES[i % DONUT_TONES.length];
    if (segs.length === 1) {
      return <circle key={`${s.label}-${i}`} className={`t-donut-seg t-donut-seg--${tone}`} cx={cx} cy={cy} r={rmid} />;
    }
    const sweep = (s.value / total) * usable;
    const from = cursor;
    const to = cursor - sweep;
    cursor = to - gapDeg;
    const [x0, y0] = point(from);
    const [x1, y1] = point(to);
    return (
      <path
        key={`${s.label}-${i}`}
        className={`t-donut-seg t-donut-seg--${tone}`}
        d={`M${x0.toFixed(1)} ${y0.toFixed(1)} A${rmid} ${rmid} 0 ${sweep > 180 ? 1 : 0} 1 ${x1.toFixed(1)} ${y1.toFixed(1)}`}
      />
    );
  });
  const fmtShare = props.formatShare ?? ((f: number): string => `${Math.round(f * 100)}%`);
  return (
    <div className="t-donut" role="img" aria-label={props.ariaLabel}>
      <div className="t-donut-ring">
        <svg className="t-donut-svg" viewBox="0 0 200 200" aria-hidden="true" preserveAspectRatio="xMidYMid meet">
          {arcs}
        </svg>
        <div className="t-donut-center" aria-hidden="true">
          <b className="t-donut-numeral">{props.centerValue}</b>
          {props.centerLabel !== undefined && <span className="t-donut-centerlabel">{props.centerLabel}</span>}
        </div>
      </div>
      <ul className="t-donut-legend" aria-hidden="true">
        {segs.map((s, i) => (
          <li key={`${s.label}-${i}`} className="t-donut-item">
            <span className={`t-donut-swatch t-donut-swatch--${s.tone ?? DONUT_TONES[i % DONUT_TONES.length]}`} />
            <span className="t-donut-name">{s.label}</span>
            <span className="t-donut-share">{fmtShare(s.value / total)}</span>
          </li>
        ))}
      </ul>
      <p className="t-footnote t-donut-note">{props.footnote}</p>
    </div>
  );
}

/* -------------------------------------------------------- ComparisonBars */

export interface ComparisonRow {
  readonly label: string;
  readonly value: number;
  /** THE outlier wears a status tone; every other bar stays neutral (§5). */
  readonly outlier?: StatusTone;
}

/**
 * The §5 comparison: thin tracks, neutral fills, the one outlier in its
 * status color, values right-aligned, footnote naming the outlier.
 */
export function ComparisonBars(props: {
  readonly rows: readonly ComparisonRow[];
  readonly footnote: string;
  readonly ariaLabel: string;
  readonly formatValue?: (value: number) => string;
}): ReactNode {
  const rows = props.rows.filter((r) => Number.isFinite(r.value) && r.value >= 0);
  if (rows.length === 0) return emptyChart("t-cmpbars", props.ariaLabel);
  const hi = Math.max(...rows.map((r) => r.value)) || 1;
  const fmt = props.formatValue ?? ((v: number): string => String(v));
  return (
    <div className="t-cmpbars" role="img" aria-label={props.ariaLabel}>
      {rows.map((row, i) => (
        <div key={`${row.label}-${i}`} className="t-cmpbars-row" aria-hidden="true">
          <span className="t-cmpbars-name">{row.label}</span>
          <span className="t-cmpbars-track">
            <span
              className={row.outlier === undefined ? "t-cmpbars-fill" : `t-cmpbars-fill t-cmpbars-fill--${row.outlier}`}
              style={{ width: pctString(row.value / hi) }}
            />
          </span>
          <span className="t-cmpbars-value">{fmt(row.value)}</span>
        </div>
      ))}
      <p className="t-footnote t-cmpbars-note">{props.footnote}</p>
    </div>
  );
}

/* -------------------------------------------------------- StackedColumns */

/** A stack's tones: the three §5 inks, and a faint fourth for "everything else". */
/** B785 (530:482) — the channel inks join: teal, violet, amber and a 35 % neutral. */
export type StackTone = ChartTone | "faint" | "teal" | "violet" | "amber" | "dim";
const STACK_TONES: readonly StackTone[] = ["accent", "ink", "muted", "faint"];

export interface StackedSeries {
  readonly key: string;
  readonly label: string;
  readonly tone?: StackTone;
}

export interface StackedColumn {
  readonly label: string;
  /** series key → value; a missing key is zero. */
  readonly values: Readonly<Record<string, number>>;
  /** B594 (5) — a column of the window BEFORE: drawn faded, on the same scale. */
  readonly prior?: boolean;
}

/**
 * B525 (c3) — 530:482's CHANNEL MIX OVER TIME: one column per period,
 * stacked by series, every column on ONE scale (the tallest total), the
 * total on top, the period below, a legend of the series. The drawing is
 * aria-hidden; the caller's ariaLabel says it in words. Nothing to stack
 * draws the house empty face rather than a row of zero-height columns.
 * A column flagged `prior` is the window before (B594 (5) — the frame's
 * "prior weeks faded"): drawn faded, on the one shared scale, so the two
 * windows read as one picture.
 */
export function StackedColumns(props: {
  readonly series: readonly StackedSeries[];
  readonly columns: readonly StackedColumn[];
  readonly footnote: string;
  readonly ariaLabel: string;
  readonly formatValue?: (value: number) => string;
}): ReactNode {
  const valueOf = (col: StackedColumn, key: string): number => {
    const v = col.values[key];
    return v !== undefined && Number.isFinite(v) && v > 0 ? v : 0;
  };
  const totals = props.columns.map((c) => props.series.reduce((sum, s) => sum + valueOf(c, s.key), 0));
  const hi = Math.max(0, ...totals);
  if (props.columns.length === 0 || hi <= 0) return emptyChart("t-stackcols", props.ariaLabel);
  const fmt = props.formatValue ?? ((v: number): string => String(v));
  const toneOf = (s: StackedSeries, i: number): StackTone => s.tone ?? STACK_TONES[i % STACK_TONES.length] ?? "accent";
  return (
    <div className="t-stackcols" role="img" aria-label={props.ariaLabel}>
      {/* B785 (530:482) — the legend leads the plot. */}
      <ul className="t-stackcols-legend" aria-hidden="true">
        {props.series.map((s, si) => (
          <li key={s.key}>
            <span className={`t-stackcols-swatch t-stackcols-seg--${toneOf(s, si)}`} />
            {s.label}
          </li>
        ))}
      </ul>
      <div className="t-stackcols-cols" aria-hidden="true">
        {props.columns.map((c, ci) => (
          <div key={`${c.label}-${ci}`} className={`t-stackcols-col${c.prior === true ? " t-stackcols-col--prior" : ""}`}>
            <span className="t-stackcols-value">{fmt(totals[ci] ?? 0)}</span>
            <span className="t-stackcols-bar">
              {props.series.map((s, si) => {
                const v = valueOf(c, s.key);
                return v === 0 ? null : (
                  <span key={s.key} className={`t-stackcols-seg t-stackcols-seg--${toneOf(s, si)}`} style={{ height: pctString(v / hi) }} />
                );
              })}
            </span>
            <span className="t-stackcols-label">{c.label}</span>
          </div>
        ))}
      </div>
      <p className="t-footnote t-stackcols-note">{props.footnote}</p>
    </div>
  );
}

/* -------------------------------------------------------- GroupedColumns */

/** A grouped bar's ink: the three chart inks, or a status tone when the series IS a verdict. */
export type GroupTone = ChartTone | StatusTone;

export interface GroupedSeries {
  readonly key: string;
  readonly label: string;
  readonly tone?: GroupTone;
}

/**
 * B708 (4) — 640:2's RENEWALS VS LAPSES · BY MONTH: one group per period,
 * the series SIDE BY SIDE (a stack would hide that lapses are a different
 * kind of count from renewals), every bar on ONE scale — the tallest single
 * value, never a group's total — the period below, a legend of the series.
 * Each series keeps its slot in every group (a zero is an empty slot), so
 * the groups stay aligned and a missing bar reads as "none". The drawing is
 * aria-hidden; the caller's ariaLabel says it in words. Nothing above zero
 * draws the house empty face.
 */
export function GroupedColumns(props: {
  readonly series: readonly GroupedSeries[];
  readonly columns: readonly { readonly label: string; readonly values: Readonly<Record<string, number>> }[];
  readonly footnote: string;
  readonly ariaLabel: string;
}): ReactNode {
  const valueOf = (values: Readonly<Record<string, number>>, key: string): number => {
    const v = values[key];
    return v !== undefined && Number.isFinite(v) && v > 0 ? v : 0;
  };
  const hi = Math.max(0, ...props.columns.flatMap((c) => props.series.map((s) => valueOf(c.values, s.key))));
  if (props.columns.length === 0 || hi <= 0) return emptyChart("t-groupcols", props.ariaLabel);
  const toneOf = (s: GroupedSeries, i: number): GroupTone => s.tone ?? (["accent", "ink", "muted"] as const)[i % 3] ?? "accent";
  return (
    <div className="t-groupcols" role="img" aria-label={props.ariaLabel}>
      <div className="t-groupcols-plot" aria-hidden="true">
        {props.columns.map((c, ci) => (
          <div key={`${c.label}-${ci}`} className="t-groupcols-group">
            <span className="t-groupcols-bars">
              {props.series.map((s, si) => (
                <span key={s.key} className={`t-groupcols-bar t-groupcols-bar--${toneOf(s, si)}`} style={{ height: pctString(valueOf(c.values, s.key) / hi) }} />
              ))}
            </span>
            <span className="t-groupcols-label">{c.label}</span>
          </div>
        ))}
      </div>
      <ul className="t-groupcols-legend" aria-hidden="true">
        {props.series.map((s, si) => (
          <li key={s.key}>
            <span className={`t-groupcols-swatch t-groupcols-bar--${toneOf(s, si)}`} />
            {s.label}
          </li>
        ))}
      </ul>
      <p className="t-footnote t-groupcols-note">{props.footnote}</p>
    </div>
  );
}

/* ------------------------------------------------------- ForecastColumns */

/**
 * The §5 forecast columns: outline bars (translucent fill, strong
 * stroke), value on top, day label below.
 */
export function ForecastColumns(props: {
  readonly columns: readonly ChartPoint[];
  readonly footnote: string;
  readonly ariaLabel: string;
  readonly formatValue?: (value: number) => string;
}): ReactNode {
  const cols = props.columns.filter((c) => Number.isFinite(c.value) && c.value >= 0);
  if (cols.length === 0) return emptyChart("t-forecast", props.ariaLabel);
  const hi = Math.max(...cols.map((c) => c.value)) || 1;
  const fmt = props.formatValue ?? ((v: number): string => String(v));
  return (
    <div className="t-forecast" role="img" aria-label={props.ariaLabel}>
      <div className="t-forecast-cols" aria-hidden="true">
        {cols.map((c, i) => (
          <div key={`${c.label}-${i}`} className="t-forecast-col">
            <span className="t-forecast-value">{fmt(c.value)}</span>
            <span className="t-forecast-bar" style={{ height: pctString(c.value / hi) }} />
            <span className="t-forecast-day">{c.label}</span>
          </div>
        ))}
      </div>
      <p className="t-footnote t-forecast-note">{props.footnote}</p>
    </div>
  );
}

/* ------------------------------------------------------------ PulseStrip */

/**
 * B379 — TODAY'S PULSE (Figma 113:2): one thin bar per hour of the day,
 * the now-hour marked, first/now/last hours labeled in the §5 axis role.
 * Counts are the fact; a day with nothing yet is the honest empty chart.
 */
export function PulseStrip(props: {
  readonly hours: readonly { readonly hour: number; readonly count: number }[];
  readonly nowHour: number;
  readonly nowWords: string;
  readonly ariaLabel: string;
}): ReactNode {
  const total = props.hours.reduce((n, h) => n + h.count, 0);
  if (props.hours.length === 0 || total === 0) return emptyChart("t-pulse", props.ariaLabel);
  const max = Math.max(...props.hours.map((h) => h.count));
  // B781 — 113:2 draws the strip as ONE row: the bars, then their axis.
  // Spans, not an SVG: a viewBox scaled into the 20-tall strip shrank the
  // whole drawing to a sliver in its middle. A bar's height is its share
  // of the busiest hour; an hour with nothing keeps the 2px floor.
  return (
    <figure className="t-pulse t-chart" role="img" aria-label={props.ariaLabel}>
      <span className="t-pulse-bars" aria-hidden="true">
        {props.hours.map((h) => (
          <span
            key={h.hour}
            className={`t-pulse-bar${h.hour === props.nowHour ? " t-pulse-bar--now" : h.count === 0 ? " t-pulse-bar--zero" : ""}`}
            {...(h.count === 0 ? {} : { style: { height: `${Math.round((h.count / max) * 100)}%` } })}
          />
        ))}
      </span>
      <figcaption className="t-pulse-axis">
        <span>{String(props.hours[0]?.hour ?? 0).padStart(2, "0")}</span>
        <span className="t-pulse-now">{props.nowWords}</span>
        <span>{String(props.hours[props.hours.length - 1]?.hour ?? 23).padStart(2, "0")}</span>
      </figcaption>
    </figure>
  );
}

/* ---------------------------------------------------------- LiveOrdersMap */

/** Deterministic 0..1 hash — layout only, never a fact (B355 map). */
function lomHash(seed: string): number {
  let h = 2166136261;
  for (let i = 0; i < seed.length; i++) {
    h ^= seed.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return ((h >>> 0) % 10_000) / 10_000;
}

/**
 * B355/B379 — the LIVE ORDERS map (today.png): the tenant's terminals as
 * ⊙ markers, dashed routes, today's orders as pulses, one frosted tooltip
 * for the newest order, the §9.7 footnote. A DIAGRAM, not geography:
 * positions are a deterministic layout over the ids (the same tenant
 * always draws the same picture); every FACT comes from a read. The big
 * soft discs are decoration on the canvas — aria-hidden, carrying nothing.
 */
export function LiveOrdersMap(props: {
  /** B387: a terminal may carry its location's own coordinate; see liveOrdersFrame. */
  readonly terminals: readonly LiveOrdersTerminal[];
  readonly routes: readonly (readonly [string, string])[];
  readonly pulses: readonly LiveOrdersPulse[];
  readonly tooltip?: {
    readonly title: string;
    readonly code: string;
    readonly amount: string;
    readonly line: string;
  };
  readonly footnote: string;
  readonly ariaLabel: string;
  /** A real tile ground behind the diagram (one static image; B379's URL). */
  readonly basemapUrl?: string;
  /**
   * RULING 2026-08-18 ("make the map on Live Orders interactive"): with a
   * token the card is a real mapbox-gl map — pins pan with the ground,
   * cooperative gestures, our controls. Absent, the flat diagram stands.
   */
  readonly live?: { readonly token: string; readonly styleUrl?: string };
}): ReactNode {
  if (props.terminals.length === 0) {
    return (
      <div className="t-lom t-chart t-chart--empty" role="img" aria-label={props.ariaLabel}>
        <span className="t-chart-note">
          The map draws your locations and today&rsquo;s orders across them — locations land here from
          your own services the moment one names where it runs.
        </span>
      </div>
    );
  }
  /* B387: the frame decides — MEASURED coordinates when every terminal
     carries one (the camera opens over them). B787 — an UNMEASURED frame no
     longer draws over real geography: the hashed layout under the US camera
     put a Santa Barbara zoo in Kansas and its order in New Mexico (measured
     2026-10-12). Without a coordinate the flat diagram stands, with the
     footnote saying what puts the place on the map; see liveOrdersFrame. */
  const frame = liveOrdersFrame({ terminals: props.terminals, pulses: props.pulses });
  /* B787 — the foot says why the venue is missing when a real map was on offer
     (the diagram face, or — B793 — a live map drawn over buyer points alone). */
  const unplaced = props.live !== undefined ? props.terminals.filter((t) => t.lngLat === undefined).map((t) => t.label) : [];
  const footnote =
    unplaced.length === 0
      ? props.footnote
      : `${props.footnote} ${unplaced.length === 1 ? `${unplaced[0] ?? "This place"} has` : `${unplaced.slice(0, 3).join(", ")}${unplaced.length > 3 ? " and others" : ""} have`} no map coordinate yet — add it on the service, under Selling → Services, and ${frame.measured ? "it joins the buyers on the map" : "this becomes a real map"}.`;
  if (props.live !== undefined && frame.measured) {
    const { camera, pins } = frame;
    // The tooltip speaks about the NEWEST order — the one pulse that is fresh (B355's clock-free rule).
    // B793: the buyer's own dot first; the venue pulse when the order has no point.
    const newest =
      props.tooltip === undefined
        ? undefined
        : (pins.find((p) => p.kind === "buyer" && p.tone === "fresh") ??
          pins.find((p) => p.kind === "pulse" && p.tone === "fresh") ??
          pins.find((p) => p.kind === "buyer" || p.kind === "pulse"));
    return (
      <figure className="t-lom t-chart t-lom--live">
        <div className="t-lom-stage">
          <LiveMap
            token={props.live.token}
            {...(props.live.styleUrl !== undefined ? { styleUrl: props.live.styleUrl } : {})}
            preset="quiet"
            camera={camera}
            pins={pins}
            {...(newest !== undefined ? { anchor: newest.lngLat } : {})}
            ariaLabel={props.ariaLabel}
          >
            {props.tooltip !== undefined ? (
              <div className="t-lom-tip">
                <p className="t-lom-tip-title">{props.tooltip.title}</p>
                <p className="t-lom-tip-row">
                  <code className="t-lom-tip-code">{props.tooltip.code}</code>
                  <span className="t-lom-tip-amount">{props.tooltip.amount}</span>
                </p>
                <p className="t-lom-tip-line">{props.tooltip.line}</p>
                <span className="t-lom-tip-leader" aria-hidden="true" />
              </div>
            ) : null}
          </LiveMap>
        </div>
        <figcaption className="t-footnote t-lom-note">{footnote}</figcaption>
      </figure>
    );
  }
  const W = 920;
  const H = 340;
  const at = new Map<string, { x: number; y: number }>();
  props.terminals.forEach((t, i) => {
    // spread across the width, wandering in the LOWER band by id — layout,
    // not a claim; the upper band stays clear for the tooltip (review #2).
    const x = 90 + ((W - 200) * (i + lomHash(t.id) * 0.5)) / Math.max(1, props.terminals.length - 0.5);
    const y = 150 + (H - 200) * lomHash(`${t.id}:y`);
    at.set(t.id, { x, y });
  });
  const pulsePoint = (id: string): { x: number; y: number } => {
    if (props.routes.length > 0) {
      const r = props.routes[Math.floor(lomHash(id) * props.routes.length)] as readonly [string, string];
      const a = at.get(r[0]);
      const b = at.get(r[1]);
      if (a !== undefined && b !== undefined) {
        const t = 0.2 + 0.6 * lomHash(`${id}:t`);
        return { x: a.x + (b.x - a.x) * t, y: a.y + (b.y - a.y) * t + (lomHash(`${id}:o`) - 0.5) * 60 };
      }
    }
    return { x: 80 + (W - 160) * lomHash(id), y: 60 + (H - 120) * lomHash(`${id}:y`) };
  };
  return (
    <figure className="t-lom t-chart" role="img" aria-label={props.ariaLabel}>
      <div className="t-lom-stage">
      {props.basemapUrl !== undefined ? <img className="t-lom-basemap" src={props.basemapUrl} alt="" aria-hidden="true" /> : null}
      <svg viewBox={`0 0 ${W} ${H}`} width="100%" aria-hidden="true" preserveAspectRatio="xMidYMid meet">
        {/* decoration: two soft discs so the glass reads over variation */}
        <circle className="t-lom-isle" cx={W * 0.22} cy={H * 0.28} r={110} />
        <circle className="t-lom-isle" cx={W * 0.82} cy={H * 0.72} r={140} />
        {props.routes.map((r, i) => {
          const a = at.get(r[0]);
          const b = at.get(r[1]);
          if (a === undefined || b === undefined) return null;
          return <line key={i} className="t-lom-route" x1={a.x} y1={a.y} x2={b.x} y2={b.y} />;
        })}
        {props.pulses.map((pulse) => {
          const p = pulsePoint(pulse.id);
          return <circle key={pulse.id} className={`t-lom-pulse t-lom-pulse--${pulse.tone}`} cx={p.x.toFixed(1)} cy={p.y.toFixed(1)} r={6} />;
        })}
        {props.terminals.map((t) => {
          const p = at.get(t.id) as { x: number; y: number };
          return (
            <g key={t.id} className="t-lom-terminal">
              <circle className="t-lom-terminal-ring" cx={p.x} cy={p.y} r={9} />
              <circle className="t-lom-terminal-dot" cx={p.x} cy={p.y} r={3} />
              <text className="t-lom-terminal-name" x={p.x + 16} y={p.y + 4}>
                {t.label}
              </text>
            </g>
          );
        })}
      </svg>
      {props.tooltip !== undefined ? (
        <div className="t-lom-tip">
          <p className="t-lom-tip-title">{props.tooltip.title}</p>
          <p className="t-lom-tip-row">
            <code className="t-lom-tip-code">{props.tooltip.code}</code>
            <span className="t-lom-tip-amount">{props.tooltip.amount}</span>
          </p>
          <p className="t-lom-tip-line">{props.tooltip.line}</p>
          <span className="t-lom-tip-leader" aria-hidden="true" />
        </div>
      ) : null}
      </div>
      <figcaption className="t-footnote t-lom-note">{footnote}</figcaption>
    </figure>
  );
}
